(() => {
  if (window.shippingProtectionInitialized) return;
  window.shippingProtectionInitialized = true;

  const STORAGE_KEY = "shippingProtectionOptOut";
  const AUTO_ADDED_KEY = "shippingProtectionAutoAdded";
  const PROTECTION_HANDLE = "shipping-protection";
  let requestInProgress = false;

  const root = () =>
    window.Shopify?.routes?.root || "/";

  const getCart = async () => {
    const response = await fetch(`${root()}cart.js`, {
      headers: { Accept: "application/json" }
    });

    if (!response.ok) throw new Error("Unable to read the cart.");
    return response.json();
  };

  const setLoading = (loading) => {
    document.querySelectorAll("[data-shipping-protection]").forEach((container) => {
      container.classList.toggle("is-loading", loading);

      const spinner = container.querySelector("[data-shipping-protection-loading]");
      if (spinner) spinner.hidden = !loading;

      container.querySelectorAll("[data-shipping-protection-toggle]").forEach((toggle) => {
        toggle.disabled = loading;
      });
    });
  };

  const clearErrors = () => {
    document.querySelectorAll("[data-shipping-protection-error]").forEach((box) => {
      box.hidden = true;
      box.textContent = "";
    });
  };

  const showError = (message) => {
    document.querySelectorAll("[data-shipping-protection-error]").forEach((box) => {
      box.textContent = message;
      box.hidden = false;
    });
  };

  const syncCheckboxes = (checked) => {
    document.querySelectorAll("[data-shipping-protection-toggle]").forEach((toggle) => {
      toggle.checked = checked;
    });
  };

  const setInitializing = (initializing) => {
    document.querySelectorAll("[data-shipping-protection]").forEach((container) => {
      container.classList.toggle("is-initializing", initializing);
    });
  };

  const getProtectionItems = (cart) =>
    cart.items.filter(
      (item) =>
        item.handle === PROTECTION_HANDLE ||
        item.properties?._shipping_protection === "true"
    );

  const getRenderContext = () => {
    const onCartPage = Boolean(isCartPage());

    if (onCartPage) {
      const cartItems = document.getElementById("main-cart-items");
      const cartFooter = document.getElementById("main-cart-footer");

      return {
        type: "cart-page",
        itemsSectionId: cartItems?.dataset.id || "main-cart-items",
        footerSectionId: cartFooter?.dataset.id || "main-cart-footer",
        sections: [
          cartItems?.dataset.id || "main-cart-items",
          cartFooter?.dataset.id || "main-cart-footer",
          "cart-icon-bubble"
        ],
        sectionsUrl: window.location.pathname || "/cart"
      };
    }

    return {
      type: "cart-drawer",
      sections: ["cart-drawer", "cart-icon-bubble"],
      sectionsUrl: window.location.pathname || "/"
    };
  };

  const renderMutationSections = (sections, context) => {
    if (!sections || !context) return;

    if (context.type === "cart-page") {
      replaceSection("main-cart-items", sections[context.itemsSectionId]);
      replaceSection("main-cart-footer", sections[context.footerSectionId]);
      replaceCartIconBubble(sections["cart-icon-bubble"]);
    } else {
      replaceCartDrawer(sections["cart-drawer"]);
      replaceCartIconBubble(sections["cart-icon-bubble"]);
    }

    document.dispatchEvent(new CustomEvent("cart:updated"));
  };

  const withSections = (payload, context) => ({
    ...payload,
    sections: context.sections,
    sections_url: context.sectionsUrl
  });

  const setLineQuantity = async (lineKey, quantity, context = null) => {
    const body = context
      ? withSections({ id: lineKey, quantity }, context)
      : { id: lineKey, quantity };

    const response = await fetch(`${root()}cart/change.js`, {
      method: "POST",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache"
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(
        result.description || result.message || "Unable to update protection."
      );
    }

    return response.json();
  };


  const normalizeProtectionQuantity = async (cart) => {
    const protectionItems = getProtectionItems(cart);
    if (protectionItems.length === 0) return cart;

    const primaryItem = protectionItems[0];

    if (primaryItem.quantity !== 1) {
      await setLineQuantity(primaryItem.key, 1);
    }

    for (let index = 1; index < protectionItems.length; index += 1) {
      await setLineQuantity(protectionItems[index].key, 0);
    }

    if (
      primaryItem.quantity !== 1 ||
      protectionItems.length > 1
    ) {
      return getCart();
    }

    return cart;
  };

  const addProtection = async (variantId, context = null) => {
    const payload = {
      items: [{
        id: Number(variantId),
        quantity: 1,
        properties: { _shipping_protection: "true" }
      }]
    };

    const body = context ? withSections(payload, context) : payload;

    const response = await fetch(`${root()}cart/add.js`, {
      method: "POST",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache"
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.description || result.message || "Unable to add protection.");
    }

    return response.json();
  };


  const removeProtection = async (lineKey, context = null) =>
    setLineQuantity(lineKey, 0, context);


  const replaceCartIconBubble = (html) => {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const next = parsed.querySelector("#cart-icon-bubble");
    const current = document.querySelector("#cart-icon-bubble");
    if (next && current) current.innerHTML = next.innerHTML;
  };

  const replaceCartDrawer = (html) => {
    if (!html) return;

    const currentDrawer = document.querySelector("cart-drawer");
    if (!currentDrawer) return;

    const wasOpen = currentDrawer.classList.contains("active");
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const nextDrawer = parsed.querySelector("cart-drawer");
    if (!nextDrawer) return;

    currentDrawer.innerHTML = nextDrawer.innerHTML;
    currentDrawer.classList.toggle("is-empty", nextDrawer.classList.contains("is-empty"));

    if (wasOpen && typeof currentDrawer.open === "function") {
      requestAnimationFrame(() => currentDrawer.open());
    }
  };

  const replaceSection = (sectionId, html) => {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, "text/html");
    const current = document.getElementById(sectionId);
    const next = parsed.getElementById(sectionId);

    if (current && next) {
      current.replaceWith(next);
    }
  };

  const refreshCartPage = async () => {
    const cartItems = document.getElementById("main-cart-items");
    const cartFooter = document.getElementById("main-cart-footer");

    const itemsSectionId = cartItems?.dataset.id || "main-cart-items";
    const footerSectionId = cartFooter?.dataset.id || "main-cart-footer";

    const requestedSections = [
      itemsSectionId,
      footerSectionId,
      "cart-icon-bubble"
    ];

    const requestUrl =
      `${root()}cart?sections=${encodeURIComponent(requestedSections.join(","))}` +
      `&_shipping_protection=${Date.now()}`;

    const response = await fetch(requestUrl, {
      method: "GET",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache"
      }
    });

    if (!response.ok) throw new Error("Unable to refresh the cart page.");

    const sections = await response.json();

    replaceSection("main-cart-items", sections[itemsSectionId]);
    replaceSection("main-cart-footer", sections[footerSectionId]);
    replaceCartIconBubble(sections["cart-icon-bubble"]);

    document.dispatchEvent(new CustomEvent("cart:updated"));
  };

  const refreshCartDrawer = async () => {
    const response = await fetch(`${root()}?sections=cart-drawer,cart-icon-bubble`, {
      headers: { Accept: "application/json" }
    });

    if (!response.ok) throw new Error("Unable to refresh the cart.");

    const sections = await response.json();
    replaceCartDrawer(sections["cart-drawer"]);
    replaceCartIconBubble(sections["cart-icon-bubble"]);
  };

  const isCartPage = () =>
    window.location.pathname.replace(/\/+$/, "") === "/cart" ||
    document.querySelector("#main-cart-items") ||
    document.querySelector("#main-cart-footer");

  const updateProtection = async (toggle) => {
    if (requestInProgress) return;

    const container = toggle.closest("[data-shipping-protection]");
    if (!container) return;

    const variantId = container.dataset.variantId;
    const requestedState = toggle.checked;

    if (!variantId) {
      toggle.checked = !requestedState;
      showError("Shipping protection is currently unavailable.");
      return;
    }

    requestInProgress = true;
    clearErrors();
    setLoading(true);
    syncCheckboxes(requestedState);
    setInitializing(true);

    try {
      const cart = await getCart();
      const protectionItems = getProtectionItems(cart);
      const protectionItem = protectionItems[0];
      const context = getRenderContext();
      let mutationResult = null;

      if (requestedState) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.setItem(AUTO_ADDED_KEY, "true");

        if (!protectionItem) {
          mutationResult = await addProtection(variantId, context);
        } else if (protectionItem.quantity !== 1) {
          mutationResult = await setLineQuantity(protectionItem.key, 1, context);
        }

        // Remove any duplicated protection lines, then obtain one final
        // section response from the last mutation.
        for (let index = 1; index < protectionItems.length; index += 1) {
          mutationResult = await setLineQuantity(
            protectionItems[index].key,
            0,
            context
          );
        }
      } else {
        sessionStorage.setItem(STORAGE_KEY, "true");
        sessionStorage.setItem(AUTO_ADDED_KEY, "true");

        if (protectionItem) {
          mutationResult = await removeProtection(protectionItem.key, context);
        }
      }

      if (mutationResult?.sections) {
        renderMutationSections(mutationResult.sections, context);
      } else {
        // No mutation was needed, but keep both controls synchronized.
        syncCheckboxes(requestedState);
      }
    } catch (error) {
      console.error("Shipping protection error:", error);
      syncCheckboxes(!requestedState);
      showError(error.message || "Something went wrong. Please try again.");
    } finally {
      requestInProgress = false;
      setLoading(false);
      setInitializing(false);
    }
  };

  let initializationScheduled = false;

  const initializeDefaultProtection = async () => {
    if (requestInProgress) return;

    const container = document.querySelector(
      '[data-shipping-protection][data-default-enabled="true"]'
    );

    setInitializing(Boolean(container));

    try {
      let cart = await getCart();
      cart = await normalizeProtectionQuantity(cart);

      const protectionItems = getProtectionItems(cart);
      const protectionItem = protectionItems[0];
      const regularItems = cart.items.filter(
        (item) =>
          item.handle !== PROTECTION_HANDLE &&
          item.properties?._shipping_protection !== "true"
      );
      const hasRegularItems = regularItems.length > 0;

// Remove orphaned protection when the cart has no regular items.
if (!hasRegularItems) {
  // Reset the stale automatic-add state for the next cart.
  sessionStorage.removeItem(AUTO_ADDED_KEY);

  if (protectionItem) {
    requestInProgress = true;
    const context = getRenderContext();
    const result = await removeProtection(protectionItem.key, context);

    if (result?.sections) {
      renderMutationSections(result.sections, context);
    }
  }

  syncCheckboxes(false);
  return;
}

      // No rendered control means this page does not need to auto-add.
      if (!container) return;

      const variantId = container.dataset.variantId;
      if (!variantId) return;

      if (protectionItem) {
        syncCheckboxes(true);
        return;
      }

      if (
        sessionStorage.getItem(STORAGE_KEY) === "true" ||
        sessionStorage.getItem(AUTO_ADDED_KEY) === "true"
      ) {
        syncCheckboxes(false);
        return;
      }

      requestInProgress = true;
      clearErrors();
      setLoading(true);
      syncCheckboxes(true);

      const context = getRenderContext();
      const result = await addProtection(variantId, context);
      sessionStorage.setItem(AUTO_ADDED_KEY, "true");

      if (result?.sections) {
        renderMutationSections(result.sections, context);
      }
    } catch (error) {
      console.error("Shipping protection initialization error:", error);
      if (container) syncCheckboxes(false);
    } finally {
      requestInProgress = false;
      setLoading(false);
      setInitializing(false);
    }
  };

  document.addEventListener("change", (event) => {
    const toggle = event.target.closest("[data-shipping-protection-toggle]");
    if (toggle) updateProtection(toggle);
  });

  const scheduleInitialization = () => {
    if (initializationScheduled) return;
    initializationScheduled = true;

    window.setTimeout(async () => {
      initializationScheduled = false;
      await initializeDefaultProtection();
    }, 80);
  };

  const observer = new MutationObserver((mutations) => {
    const protectionAdded = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;

        return (
          node.matches?.("[data-shipping-protection]") ||
          node.querySelector?.("[data-shipping-protection]")
        );
      })
    );

    if (protectionAdded) scheduleInitialization();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  document.addEventListener("cart:updated", scheduleInitialization);
  document.addEventListener("cart:refresh", scheduleInitialization);
  window.addEventListener("pageshow", scheduleInitialization);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleInitialization();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInitialization);
  } else {
    scheduleInitialization();
  }
})();
